import React, { useEffect, useState } from 'react';
import _ from '@lodash';
import { Grid } from '@mui/material';
import { Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useFormContext } from 'react-hook-form';
import BasicLineItemRate from '../../../../../configured-components/form/configured-inputs/special/payment-line-item-rates/BasicLineItemRate';
import TieredLineItemRate from '../../../../../configured-components/form/configured-inputs/special/payment-line-item-rates/TieredLineItemRate';
import Select from '../../../../../configured-components/form/configured-inputs/select/Select';
import { ruleTypes, daysInMonth, criteriaOptions } from '../../../../../configured-components/form/configured-inputs/special/payment-line-item-rates/select-options';
import { basicRateFormDefault } from '../../../../../configured-components/form/configured-inputs/special/payment-line-item-rates/line-item-config-form-utils';
import SelectWithFetch from '../../../../../configured-components/form/configured-inputs/select/SelectWithFetch';
import BooleanSwitch from '../../../../../configured-components/form/configured-inputs/boolean/BooleanSwitch';

function EffectiveLineItemRateForm({ name, disabledOnEdit = false }) {
    const [disableRuleType, setDisableRuleType] = useState(false);
    const [averageOverrideOptions] = useState(criteriaOptions.filter((option) => !option.boolean && option.id !== 'payment_rate'));

    const { watch, setValue, formState, getValues } = useFormContext();

    const useStyles = makeStyles((theme) => ({
        basicRatesContainer: {
            width: '100%',
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(2),
            padding: theme.spacing(2),
            backgroundColor: theme.palette.background.default,
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: theme.palette.divider,
            borderRadius: theme.shape.borderRadius,
        },
    }));

    const classes = useStyles();

    // Reset the tiered rules or the rate when the ruletype has changed
    useEffect(() => {
        const isDirty = _.get(formState.dirtyFields, `${name}.rulesType`);
        if (isDirty) {
            setValue(`${name}.rules`, []);
            setValue(`${name}.rate`, basicRateFormDefault);
        }
    }, [watch(`${name}.rulesType`)]);

    const excludeProducerWatch = watch(`${name}.basis_parameter_filter.exclude_producer_ids`);
    const includeProducerWatch = watch(`${name}.basis_parameter_filter.include_producer_ids`);
    useEffect(() => {
        if (excludeProducerWatch?.length > 0) {
            setValue(`${name}.basis_parameter_filter.include_producer_ids`, null);
        }
    }, [excludeProducerWatch]);
    useEffect(() => {
        if (includeProducerWatch?.length > 0) {
            setValue(`${name}.basis_parameter_filter.exclude_producer_ids`, null);
        }
    }, [includeProducerWatch]);

    useEffect(() => {
        // When haulers are the payer, the rules are always basic, unless it's an existing rate.
        // If it's an existing rate, we check on submit for the tiered rules and display an error.
        if (getValues('ruleType') === 'haulerVendor' && getValues(`${name}._id`) == null) {
            setValue(`${name}.rulesType`, 'basic');
            setValue(`${name}.rules`, []);
            setDisableRuleType(true);
        } else {
            setDisableRuleType(false);
        }
    }, [watch('ruleType')]);

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="body1" className="font-bold">
                        Payment Rate
                    </Typography>
                </Grid>
                <Grid item xs={4}>
                    <Select name={`${name}.rulesType`} props={{ label: 'Rules', options: ruleTypes, required: true, disabled: disabledOnEdit || disableRuleType }} />
                </Grid>
            </Grid>
            {watch(`${name}.rulesType`) === 'basic' && (
                <Grid container className={classes.basicRatesContainer}>
                    <BasicLineItemRate name={`${name}.rate`} disabledOnEdit={disabledOnEdit} />
                </Grid>
            )}
            {watch(`${name}.rulesType`) === 'tiered' && <TieredLineItemRate name={name} disabledOnEdit={disabledOnEdit} />}
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="body1" className="font-bold">
                        Additional Criteria
                    </Typography>
                    <Typography variant="caption">
                        These options will filter the pickups used when calculating the value of this rate to pickups that match the selected criteria. Include Producers and Exclude Producers is only valid for rates on a Hauler - Vendor MooRule. Haulers is only valid for rates on a Producer - Vendor or Producer - Hauler MooRule.
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Select name={`${name}.basis_parameter_filter.exclude_pickups_before_date`} props={{ label: 'Exclude Pickups Before Date', options: daysInMonth, disabled: disabledOnEdit }} />
                </Grid>
                <Grid item xs={6}>
                    <Select name={`${name}.basis_parameter_filter.exclude_pickups_after_date`} props={{ label: 'Exclude Pickups After Date', options: daysInMonth, disabled: disabledOnEdit }} />
                </Grid>
                <Grid item xs={6}>
                    <SelectWithFetch
                        name={`${name}.basis_parameter_filter.processor_ids`}
                        query={{
                            endpoint: '/users/populated',
                            where: { role: 'processor', status: { neq: 'Inactive' }, subuser: { neq: true } },
                            fields: ['_id', 'name', 'license_number'],
                        }}
                        props={{
                            optionLabel: {
                                keys: ['license_number', 'name'],
                                connector: ' - ',
                            },
                            label: 'Processors',
                            multiple: true,
                            disableCloseOnSelect: true,
                            disabled: disabledOnEdit,
                        }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <SelectWithFetch
                        name={`${name}.basis_parameter_filter.route_ids`}
                        query={{
                            endpoint: '/routes',
                        }}
                        props={{
                            optionLabel: {
                                keys: ['name'],
                                connector: '',
                            },
                            label: 'Routes',
                            multiple: true,
                            disableCloseOnSelect: true,
                            disabled: disabledOnEdit,
                        }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <SelectWithFetch
                        name={`${name}.basis_parameter_filter.hauler_ids`}
                        query={{
                            endpoint: '/users/populated',
                            where: { role: 'transport', status: { neq: 'Inactive' }, subuser: { neq: true } },
                            fields: ['_id', 'name', 'hauler_number'],
                        }}
                        props={{
                            optionLabel: {
                                keys: ['hauler_number', 'name'],
                                connector: ' - ',
                            },
                            label: 'Haulers',
                            multiple: true,
                            disableCloseOnSelect: true,
                            disabled: disabledOnEdit,
                        }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <BooleanSwitch
                        name={`${name}.basis_parameter_filter.pay_to_pickup_hauler`}
                        props={{
                            label: 'Pay to Pickup Hauler',
                            disabled: disabledOnEdit,
                        }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <SelectWithFetch
                        name={`${name}.basis_parameter_filter.include_producer_ids`}
                        query={{
                            endpoint: '/users/populated',
                            where: { role: 'producer', status: { neq: 'Inactive' }, subuser: { neq: true } },
                            fields: ['_id', 'name', 'license_number'],
                        }}
                        props={{
                            optionLabel: {
                                keys: ['license_number', 'name'],
                                connector: ' - ',
                            },
                            label: 'Include Producers',
                            multiple: true,
                            disableCloseOnSelect: true,
                            disabled: disabledOnEdit,
                        }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <SelectWithFetch
                        name={`${name}.basis_parameter_filter.exclude_producer_ids`}
                        query={{
                            endpoint: '/users/populated',
                            where: { role: 'producer', status: { neq: 'Inactive' }, subuser: { neq: true } },
                            fields: ['_id', 'name', 'license_number'],
                        }}
                        props={{
                            optionLabel: {
                                keys: ['license_number', 'name'],
                                connector: ' - ',
                            },
                            label: 'Exclude Producers',
                            multiple: true,
                            disableCloseOnSelect: true,
                            disabled: disabledOnEdit,
                        }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Select name={`${name}.average_override`} props={{ label: 'Override Rate Average Display', options: averageOverrideOptions, disabled: disabledOnEdit, subtitle: 'This field displays the selected criteria on the pay statement under the Average column.' }} />
                </Grid>
            </Grid>
        </>
    );
}

export default EffectiveLineItemRateForm;
